import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Long-press to lift, then drag to another day — the touch counterpart of HTML5 drag.
 * Mouse pointers are ignored so native drag-and-drop keeps working on desktop.
 * The handlers read `lift` from the render closure, so they must not be memoised.
 */
const LIFT_MS = 420;
const CANCEL_PX = 12;
const CLICK_GUARD_MS = 450;

export const useTouchDrag = ({ enabled, onDrop, targetAttr = "data-week-day" }) => {
  const [lift, setLift] = useState(null);
  const [target, setTarget] = useState(null);
  const timer = useRef(null);
  const start = useRef(null);
  const droppedAt = useRef(0);

  const cleanup = useCallback(() => {
    clearTimeout(timer.current);
    timer.current = null;
    start.current = null;
    document.body.style.removeProperty("overflow");
    document.body.style.removeProperty("touch-action");
    setLift(null);
    setTarget(null);
  }, []);

  useEffect(() => cleanup, [cleanup]);

  const dayUnder = (x, y) => {
    const el = document.elementFromPoint(x, y);
    return el?.closest?.(`[${targetAttr}]`)?.getAttribute(targetAttr) || null;
  };

  const handlers = (event) =>
    enabled
      ? {
          onPointerDown: (e) => {
            if (e.pointerType === "mouse") return;
            const el = e.currentTarget;
            start.current = { x: e.clientX, y: e.clientY, pointerId: e.pointerId, el };
            timer.current = setTimeout(() => {
              document.body.style.overflow = "hidden";
              document.body.style.touchAction = "none";
              try {
                el.setPointerCapture(e.pointerId);
              } catch {
                /* capture is best-effort */
              }
              navigator.vibrate?.(12);
              setLift({ event, x: start.current.x, y: start.current.y });
              setTarget(dayUnder(start.current.x, start.current.y));
            }, LIFT_MS);
          },
          onPointerMove: (e) => {
            if (e.pointerType === "mouse") return;
            if (!lift) {
              const s = start.current;
              if (s && Math.hypot(e.clientX - s.x, e.clientY - s.y) > CANCEL_PX) {
                clearTimeout(timer.current);
                timer.current = null;
              }
              return;
            }
            e.preventDefault();
            setLift((l) => (l ? { ...l, x: e.clientX, y: e.clientY } : l));
            setTarget(dayUnder(e.clientX, e.clientY));
          },
          onPointerUp: (e) => {
            if (e.pointerType === "mouse") return;
            const dropDate = lift ? target : null;
            const dragged = lift?.event;
            cleanup();
            if (dropDate && dragged && dragged.date !== dropDate) {
              droppedAt.current = Date.now();
              onDrop(dragged, dropDate);
            } else if (dragged) {
              droppedAt.current = Date.now();
            }
          },
          onPointerCancel: cleanup,
          onContextMenu: (e) => {
            if (lift) e.preventDefault();
          },
        }
      : {};

  return {
    lift,
    target,
    handlers,
    ignoreClick: () => Date.now() - droppedAt.current < CLICK_GUARD_MS,
  };
};
